/**
 * 지출 상세 탐색 — 드릴다운 창 (v2.63.0).
 *
 * [지출 현황]의 계정과목별·사용자별 줄을 클릭하면 열린다.
 * 창 안에서 월·계정과목·보조과목·사용자·사용처 목록의 아무 줄이나 클릭하면
 * 그 조건이 위 경로에 쌓이며 한 단계씩 좁혀진다. 경로를 클릭하면 그 단계로 되돌아간다.
 * 맨 아래에는 항상 현재 조건의 개별 내역이 보이고, 행 클릭 = 실제 결재 문서(ExpenseDocModal).
 *
 * 데이터 = GET /expense/stats/drill — 시야·집계 기준이 /stats 와 동일(visibleSql 공용).
 * 이미 고른 축의 나눔 목록은 서버가 빈 배열로 준다 → 화면에서 자연히 사라진다.
 */

import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as api from '../lib/expense-api';
import { ExpenseDocModal, EXPENSE_STATUS_BADGE } from './ExpenseDocModal';
import { LoadingLines } from './ui/Skeleton';

const fmtKrw = (n: number): string => Math.round(n).toLocaleString('ko-KR');

/** 한 단계 좁힌 조건 — 경로(빵부스러기)에 쌓인다 */
export interface DrillCrumb {
  dim: 'month' | 'account' | 'subAccount' | 'author' | 'vendor' | 'usageTarget';
  value: string;
  /**
   * 사용대상 전용 — 한 대상이 여러 표기로 갈라져 있을 때 그 표기 전부 (차량 「6526」·「194호 6526」).
   * 없으면 value 하나로 본다.
   */
  values?: string[];
  label: string;
}

const DIM_TITLE: Record<DrillCrumb['dim'], string> = {
  month: '월별',
  account: '계정과목별',
  subAccount: '보조과목별',
  author: '사용자별',
  vendor: '사용처별',
  usageTarget: '사용대상별',
};

/** 사용대상 축의 이름 — 계정과목마다 뜻이 다르다 (지출 현황 화면과 같은 표) */
const TARGET_NOUN: Record<string, string> = {
  접대비: '거래처',
  차량유지비: '차량',
  복리후생비: '직원',
  여비교통비: '목적지',
};

interface Props {
  /** 지출 현황 화면의 현재 필터 — 탐색의 바탕 조건 (경로로 지울 수 없음) */
  base: api.ExpenseDrillParams;
  /** base.author 의 표시용 이름 — 없으면 ID 로 강등 표시 */
  baseAuthorName?: string;
  /** 클릭해 들어온 시작 경로 (예: 계정과목=차량유지비) */
  initialPath: DrillCrumb[];
  onClose: () => void;
}

export function ExpenseDrillModal({ base, baseAuthorName, initialPath, onClose }: Props) {
  const qc = useQueryClient();
  const [path, setPath] = useState<DrillCrumb[]>(initialPath);
  const [openDoc, setOpenDoc] = useState<{ id: number; type: api.ExpenseDocType } | null>(null);

  // 바탕 조건 + 경로 → 실제 조회 조건 (같은 축이 겹치면 경로가 이긴다)
  const eff = useMemo(() => {
    const p: api.ExpenseDrillParams = { ...base };
    for (const c of path) {
      if (c.dim === 'month') p.month = Number(c.value);
      else if (c.dim === 'account') p.account = c.value;
      else if (c.dim === 'subAccount') p.subAccount = c.value;
      else if (c.dim === 'author') p.author = c.value;
      else if (c.dim === 'usageTarget') p.usageTargets = c.values ?? [c.value];
      else p.vendor = c.value;
    }
    return p;
  }, [base, path]);

  const drillQuery = useQuery({
    queryKey: ['expense', 'drill', eff.year, eff.month ?? 0, eff.type ?? '', eff.dept ?? '',
      eff.author ?? '', eff.account ?? '', eff.subAccount ?? '', eff.vendor ?? '',
      eff.usageTargets ?? [],
      eff.includePending ? 1 : 0],
    queryFn: () => api.getExpenseDrill(eff),
    staleTime: 30_000,
  });
  const data = drillQuery.data ?? null;

  const push = (c: DrillCrumb) => setPath((prev) => [...prev, c]);

  // 바탕 조건 표시 칩 — 경로와 달리 여기서는 못 지운다 (지출 현황 화면의 필터)
  const baseChips: string[] = [];
  baseChips.push(`${base.year}년`);
  if (base.month) baseChips.push(`${base.month}월`);
  if (base.type) baseChips.push(base.type);
  if (base.dept) baseChips.push(base.dept);
  if (base.account) baseChips.push(base.account);
  if (base.author) baseChips.push(`사용자 ${baseAuthorName ?? base.author}`);
  if (base.includePending) baseChips.push('결재 진행 중 포함');

  // 경로가 바뀌면 나눔 패널의 "더 보기" 펼침을 초기화 (remount)
  const pathKey = path.map((c) => `${c.dim}:${c.value}`).join('|');

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded shadow-xl w-[1080px] max-w-[97vw] max-h-[92vh] flex flex-col">
        {/* 머리 */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-slate-50 rounded-t">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="ui-subsection-title">🔍 지출 상세 탐색</h3>
            <span className="text-xs text-slate-500">
              {baseChips.join(' · ')} — 아래 목록을 클릭할수록 좁혀집니다
            </span>
          </div>
          <button type="button" onClick={onClose} aria-label="닫기"
            className="ui-close">×</button>
        </div>

        {/* 경로 (빵부스러기) */}
        <div className="px-5 py-2.5 border-b border-slate-100 flex items-center gap-1 flex-wrap text-xs">
          <button type="button" onClick={() => setPath([])}
            className={`px-2 py-0.5 rounded ${path.length === 0
              ? 'bg-blue-600 text-white font-semibold'
              : 'text-blue-600 hover:bg-blue-50 underline'}`}>
            전체
          </button>
          {path.map((c, i) => (
            <span key={`${c.dim}-${c.value}`} className="flex items-center gap-1">
              <span className="text-slate-300">›</span>
              <button type="button" onClick={() => setPath(path.slice(0, i + 1))}
                className={`px-2 py-0.5 rounded ${i === path.length - 1
                  ? 'bg-blue-600 text-white font-semibold'
                  : 'text-blue-600 hover:bg-blue-50 underline'}`}>
                {c.label}
              </button>
            </span>
          ))}
          {path.length > 0 && (
            <button type="button" onClick={() => setPath(path.slice(0, -1))}
              className="ml-2 text-slate-400 hover:text-slate-600">← 한 단계 위로</button>
          )}
        </div>

        {/* 몸통 */}
        <div className="overflow-y-auto px-5 py-4 space-y-4">
          {drillQuery.isLoading ? (
            <div className="py-10 text-center text-sm text-slate-400"><LoadingLines /></div>
          ) : drillQuery.isError ? (
            <div className="py-6 text-sm text-rose-600">
              불러오지 못했습니다: {(drillQuery.error as Error).message}
            </div>
          ) : data && (
            <>
              {/* 현재 조건 합계 */}
              <div className="flex items-center gap-4 flex-wrap bg-slate-50 rounded px-4 py-3">
                <div>
                  <span className="text-xs text-slate-500 mr-2">합계</span>
                  <span className="text-lg font-bold text-slate-900 num">{fmtKrw(data.summary.amount)}원</span>
                </div>
                <div className="text-xs text-slate-500">
                  내역 <b className="num">{data.summary.itemCount}</b>건 ·
                  문서 <b className="num">{data.summary.docCount}</b>건
                </div>
                {data.usageTargets.length > 1 && (
                  <div className="text-xs text-slate-500">
                    묶인 표기 {data.usageTargets.length}가지: {data.usageTargets.map((t) => `「${t}」`).join(' ')}
                  </div>
                )}
              </div>

              {/* 1/N 배분 안내 — 복리후생비를 직원별로 볼 때 금액이 「1인분」임을 밝힌다 */}
              {data.allocated && (
                <div className="text-xs bg-sky-50 border border-sky-200 text-sky-900 rounded px-3 py-2">
                  <b>함께 쓴 내역은 인원수로 나눈 1인분 금액</b>입니다 (지출 현황의 사용대상별 상세와 같은 기준).
                  아래 표의 금액도 1인분이며, 원래 금액은 그 옆에 함께 적었습니다.
                  (나눗셈 반올림 때문에 줄별 금액을 손으로 더한 값이 합계와 몇 원 다를 수 있습니다.)
                </div>
              )}

              {data.summary.itemCount === 0 ? (
                <div className="py-8 text-center text-sm text-slate-400">
                  이 조건에 맞는 지출 내역이 없습니다 — 경로를 클릭해 되돌아가세요.
                </div>
              ) : (
                <>
                  {/* 더 나눠보기 */}
                  <div key={pathKey} className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                    {data.byMonth.length > 0 && (
                      <DrillPanel title={DIM_TITLE.month}
                        rows={data.byMonth.map((r) => ({
                          key: String(r.m), label: `${r.m}월`, amount: r.amount, count: r.count,
                        }))}
                        onPick={(row) => push({ dim: 'month', value: row.key, label: row.label })} />
                    )}
                    {data.byAccount.length > 0 && (
                      <DrillPanel title={DIM_TITLE.account}
                        rows={data.byAccount.map((r) => ({
                          key: r.account, label: r.account, amount: r.amount, count: r.count,
                        }))}
                        onPick={(row) => push({ dim: 'account', value: row.key, label: row.label })} />
                    )}
                    {data.bySubAccount.length > 0 && (
                      <DrillPanel title={DIM_TITLE.subAccount}
                        rows={data.bySubAccount.map((r) => ({
                          key: r.subAccount, label: r.subAccount, amount: r.amount, count: r.count,
                        }))}
                        onPick={(row) => push({ dim: 'subAccount', value: row.key, label: row.label })} />
                    )}
                    {data.byUser.length > 0 && (
                      <DrillPanel title={DIM_TITLE.author}
                        rows={data.byUser.map((r) => ({
                          key: r.userId, label: r.name, sub: r.dept ?? undefined,
                          amount: r.amount, count: r.count,
                        }))}
                        onPick={(row) => push({ dim: 'author', value: row.key, label: row.label })} />
                    )}
                    {data.byUsageTarget.length > 0 && (
                      <DrillPanel
                        title={`${DIM_TITLE.usageTarget}${
                          eff.account && TARGET_NOUN[eff.account] ? ` (${TARGET_NOUN[eff.account]})` : ''
                        }`}
                        rows={data.byUsageTarget.map((r) => ({
                          key: JSON.stringify(r.rawTargets),
                          label: r.label, sub: r.sub || undefined,
                          values: r.rawTargets, amount: r.amount, count: r.count,
                        }))}
                        onPick={(row) => push({
                          dim: 'usageTarget',
                          value: row.values?.[0] ?? row.label,
                          values: row.values,
                          label: row.label,
                        })} />
                    )}
                    {data.byVendor.length > 0 && (
                      <DrillPanel
                        title={data.vendorKinds > data.byVendor.length
                          ? `${DIM_TITLE.vendor} (상위 ${data.byVendor.length} / 전체 ${data.vendorKinds}곳)`
                          : DIM_TITLE.vendor}
                        rows={data.byVendor.map((r) => ({
                          key: r.vendor, label: r.vendor, amount: r.amount, count: r.count,
                        }))}
                        onPick={(row) => push({ dim: 'vendor', value: row.key, label: row.label })} />
                    )}
                  </div>

                  {/* 개별 내역 */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <h4 className="text-sm font-bold text-slate-700">
                        개별 내역 <span className="font-normal text-slate-500 text-xs">
                          — 행 클릭 = 결재 문서 열기
                        </span>
                      </h4>
                      {data.itemsTruncated && (
                        <span className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded px-2 py-0.5">
                          최근 300건만 표시 — 전체 {data.summary.itemCount}건은 조건을 더 좁혀서 보세요
                        </span>
                      )}
                    </div>
                    <div className="overflow-x-auto border border-slate-100 rounded">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-50">
                          <tr className="text-left text-xs text-slate-500">
                            <th className="py-1.5 px-2 whitespace-nowrap">일자</th>
                            <th className="py-1.5 px-2 whitespace-nowrap">문서</th>
                            <th className="py-1.5 px-2 whitespace-nowrap">작성자</th>
                            <th className="py-1.5 px-2 whitespace-nowrap">계정과목</th>
                            <th className="py-1.5 px-2">사용처</th>
                            <th className="py-1.5 px-2">내용·사용대상</th>
                            <th className="py-1.5 px-2 text-right whitespace-nowrap">금액</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.items.map((it, i) => (
                            <tr key={`${it.docId}-${i}`}
                              onClick={() => setOpenDoc({ id: it.docId, type: it.docType })}
                              className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer">
                              <td className="py-1.5 px-2 text-slate-500 whitespace-nowrap num">{it.itemDate ?? '-'}</td>
                              <td className="py-1.5 px-2 whitespace-nowrap">
                                <span className="text-blue-700">{it.docNo}</span>
                                {it.status !== '최종승인' && (
                                  <span className={`ml-1 text-[11px] px-1 py-0.5 rounded border ${EXPENSE_STATUS_BADGE[it.status] ?? ''}`}>
                                    {it.status}
                                  </span>
                                )}
                              </td>
                              <td className="py-1.5 px-2 text-slate-600 whitespace-nowrap">{it.authorName}</td>
                              <td className="py-1.5 px-2 text-slate-600 whitespace-nowrap">
                                {it.account}
                                {it.subAccount && <span className="text-slate-400"> · {it.subAccount}</span>}
                              </td>
                              <td className="py-1.5 px-2 text-slate-600">{it.vendor ?? '-'}</td>
                              <td className="py-1.5 px-2 text-slate-500">
                                {it.description ?? it.usageTarget ?? '-'}
                                {it.description && it.usageTarget && (
                                  <span className="text-slate-400"> · {it.usageTarget}</span>
                                )}
                              </td>
                              <td className={`py-1.5 px-2 text-right num whitespace-nowrap ${
                                it.amount < 0 ? 'text-rose-600' : 'text-slate-800'}`}>
                                {fmtKrw(it.amount)}원
                                {it.amount !== it.fullAmount && (
                                  <span className="block text-[11px] text-slate-500 font-normal">
                                    전체 {fmtKrw(it.fullAmount)} ÷ {it.shareParts}명
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        {!data.itemsTruncated && data.items.length > 1 && (
                          <tfoot>
                            <tr className="border-t border-slate-200 bg-slate-50 font-semibold">
                              <td colSpan={6} className="py-1.5 px-2 text-right text-slate-500">합계</td>
                              <td className="py-1.5 px-2 text-right num text-slate-900 whitespace-nowrap">
                                {fmtKrw(data.summary.amount)}원
                              </td>
                            </tr>
                          </tfoot>
                        )}
                      </table>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* 내역 행 클릭 → 실제 결재 문서 (탐색 창 위에 뜬다 — z-[70]) */}
      {openDoc && (
        <ExpenseDocModal
          docId={openDoc.id}
          docType={openDoc.type}
          onClose={() => setOpenDoc(null)}
          onChanged={() => { void qc.invalidateQueries({ queryKey: ['expense'] }); }}
        />
      )}
    </div>
  );
}

interface PanelRow {
  key: string; label: string; sub?: string; amount: number; count: number;
  /** 사용대상 전용 — 이 줄이 묶고 있는 원본 표기들 (차량 한 대 = 표기 여러 개) */
  values?: string[];
}

/** 나눔 목록 한 패널 — 상위 6줄 + [더 보기]. 줄 클릭 = 한 단계 좁히기 */
function DrillPanel({ title, rows, onPick }: {
  title: string;
  rows: PanelRow[];
  onPick: (row: PanelRow) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? rows : rows.slice(0, 6);
  const maxAmt = Math.max(...rows.map((r) => Math.abs(r.amount)), 1);

  return (
    <div className="border border-slate-200 rounded p-3">
      <div className="text-xs font-bold text-slate-600 mb-2">{title}</div>
      <div className="space-y-0.5">
        {shown.map((r) => (
          <button key={r.key} type="button" onClick={() => onPick(r)}
            className="w-full grid grid-cols-[minmax(64px,110px)_minmax(0,1fr)_max-content] items-center gap-2 text-left rounded px-1 py-0.5 hover:bg-blue-50"
            title={`${r.label}${r.sub ? ` (${r.sub})` : ''} — 클릭하면 이 조건으로 좁힙니다`}>
            <span className="text-xs text-slate-700 truncate">
              {r.label}
              {r.sub && <span className="text-slate-500 ml-1 text-[11px]">{r.sub}</span>}
            </span>
            <span className="block h-2 rounded-sm bg-slate-100 overflow-hidden">
              <span className="block h-full rounded-sm bg-blue-400"
                style={{ width: `${Math.round((Math.max(r.amount, 0) / maxAmt) * 100)}%` }} />
            </span>
            <span className="text-xs text-right num text-slate-600 whitespace-nowrap">
              {fmtKrw(r.amount)}원 <span className="text-slate-400">({r.count})</span>
            </span>
          </button>
        ))}
      </div>
      {rows.length > 6 && (
        <button type="button" onClick={() => setExpanded(!expanded)}
          className="mt-1.5 text-xs text-blue-600 hover:underline">
          {expanded ? '접기' : `${rows.length - 6}개 더 보기`}
        </button>
      )}
    </div>
  );
}
